/**
 * Voice Profile (§B) — spec-model section, one light card, all eight spec parts
 * in spec order: voice_summary (always-injected, word-counted, field-level
 * "found on site · Verify") → attributes[] → mechanics → do_say[]/never_say[]
 * → formatting_rules[] (Agile house rules, client-overridable) →
 * golden_examples[] (elevated, above rejected) → rejected_examples[] (last,
 * quiet). Ported off the wizard's StepVoice, plus two capabilities StepVoice
 * never had: do_say chips and formatting_rules toggles — contract §2.3 already
 * reserves both ChunkKinds, this is the first UI for either. ADDITIVE ONLY —
 * no backend/API/schema change; formatting-rule overrides ride the existing
 * generic bb_chunks CRUD (kind='formatting_rule', title=rule key, body='on'|'off').
 *
 * EXPLICIT SAVE (bb-phase2-explicit-save-brief-20260810): §B buffers every
 * edit locally and commits on the page's "Save B · n changes" button — see
 * useVoiceProfileSave / voiceProfileBuffer. Nothing in this file writes to the
 * network except Verify, which is an operator act, not an edit, and stays
 * immediate by contract. Rows added but not yet saved render with an UNSAVED
 * badge and carry no Verify/Reject affordance (they have no server id yet).
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useVoiceProfileSave } from './useVoiceProfileSave';
import type { VoiceRow } from './voiceProfileBuffer';
import {
  EnvelopeBadge,
  Field,
  StepCard,
  VerificationRow,
  inputClass,
} from '@/components/brand-bible-wizard/primitives';

const wordCount = (s: string) => (s.trim() ? s.trim().split(/\s+/).length : 0);

/**
 * Foreign-shape tolerance (§B L3 ruling): the UI must DISPLAY stored truth it
 * cannot edit, never render it as unset. `person` is a two-value control, but
 * enrichment has written prose into it in production (e.g. "First-person plural
 * for the brand and second person for the reader."). Neither button matches, so
 * without this the field reads empty and the next edit silently destroys real
 * data. Same tolerance the H panel adopted.
 */
export const isEnumPerson = (v?: string | null): boolean => {
  const s = (v ?? '').trim().toLowerCase();
  return s === 'we' || s === 'i';
};

/** The stored person value when it is present but NOT one of the enum options. */
export const foreignPerson = (v?: string | null): string | null => {
  const s = (v ?? '').trim();
  return s && !isEnumPerson(s) ? s : null;
};

/**
 * A formatting_rule row whose title matches no house key must still be visible.
 * Production holds one with an empty title and prose in `body`; the
 * title===key lookup hid it entirely while §B still scored full marks.
 */
export const formattingFallbackTitle = (title?: string | null, body?: string | null): string => {
  const t = (title ?? '').trim();
  if (t) return t;
  const b = (body ?? '').trim();
  if (!b) return '(untitled rule)';
  return b.length > 40 ? `${b.slice(0, 40)}…` : b;
};

/**
 * `public_mention=false` rejected_example rows are internal audit notes
 * (Slack thread ids, cross-refs). They must not render as client-rejected copy.
 * Still visible — stored truth is not hidden — but labeled internal.
 */
export const isInternalRejectedNote = (row: { public_mention?: boolean | null }): boolean =>
  row.public_mention === false;

/**
 * Agile house defaults for formatting. No formatting_rule chunk for a key =
 * inherited (house default applies); a chunk titled with the key carries the
 * client's override in `body` ('on' | 'off'). Kept short and legible rather
 * than exhaustive — additive UI only, not a style-guide rewrite.
 */
const HOUSE_FORMATTING_RULES: { key: string; label: string; houseDefault: 'on' | 'off' }[] = [
  { key: 'oxford_comma', label: 'Oxford comma', houseDefault: 'on' },
  { key: 'sentence_case_headings', label: 'Sentence case headings (not Title Case)', houseDefault: 'on' },
  { key: 'active_voice', label: 'Active voice preferred', houseDefault: 'on' },
  { key: 'contractions_in_body', label: 'Contractions allowed in body copy', houseDefault: 'on' },
];

/** Title match for a house rule. Trimmed on BOTH sides — the old exact-match
 *  lookup and the trimmed unmapped-row filter disagreed, so a row titled
 *  " oxford_comma " counted as neither an override nor an unmapped row and
 *  rendered nowhere at all. */
const matchesHouseKey = (row: VoiceRow, key: string) => (row.title ?? '').trim() === key;

const UnsavedBadge = () => <EnvelopeBadge kind="discovered">unsaved</EnvelopeBadge>;

/** Chip-scale variant — a colour change alone is not a signal, so a buffered
 *  chip says so in words as well as in its dashed outline. */
const UnsavedTag = () => (
  <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">unsaved</span>
);

export function VoiceProfilePanel({
  canVerify,
  legacyToneFallback,
}: {
  canVerify: boolean;
  /**
   * READ-THROUGH ONLY, one direction, never written back — same alias contract
   * as `theme_config` deriving from `primary_colors`/`typography` elsewhere on
   * this page. `tone_of_writing` stays the canonical legacy field with its own
   * editable box and its own save path; this only fills the summary's starting
   * content on load for a tenant who has legacy tone text but no bb_voice_profile
   * row yet, so the field never LOOKS blank when data already exists. Editing
   * and saving the summary writes ONLY to voice_profile.voice_summary — it must
   * never mutate `tone_of_writing` (Carl: legacy values are never destructively
   * overwritten by a new-field edit).
   */
  legacyToneFallback?: string;
}) {
  const {
    voice,
    verifyEntry,
    bbTenant,
    buffer,
    actions,
    storedScalar,
    scalar,
    attributes,
    doSay,
    neverSay,
    formattingRows,
    golden,
    rejected,
  } = useVoiceProfileSave(legacyToneFallback);

  const [newAttr, setNewAttr] = useState('');
  const [newDo, setNewDo] = useState('');
  const [newNever, setNewNever] = useState('');
  const [gx, setGx] = useState({ body: '', channel: '', why: '' });
  const [newRejected, setNewRejected] = useState({ body: '', why: '' });

  const summaryText = scalar('voice_summary');
  const summaryWords = wordCount(summaryText);

  // The singleton draft IS the field — same "found on site · Verify" affordance
  // §A uses for a duplicated legal_name, so it does not also camp in a wall.
  const isDraftSummary = voice.entry?.source === 'discovered' && !voice.entry?.verified_by;

  // Stored truth, NOT the buffered view — the "custom (from enrichment)" chip
  // must keep showing what is on the server until a Save actually replaces it.
  const storedForeignPerson = foreignPerson(voice.entry?.person);
  const personView = scalar('person');

  const addGolden = () => {
    if (!gx.body.trim()) return;
    actions.addRow({
      kind: 'golden_example',
      body: gx.body.trim(),
      channel: gx.channel.trim() || undefined,
      why: gx.why.trim() || undefined,
    });
    setGx({ body: '', channel: '', why: '' });
  };

  const addRejected = () => {
    if (!newRejected.body.trim()) return;
    actions.addRow({
      kind: 'rejected_example',
      body: newRejected.body.trim(),
      why: newRejected.why.trim() || undefined,
    });
    setNewRejected({ body: '', why: '' });
  };

  const toggleFormatting = (rule: (typeof HOUSE_FORMATTING_RULES)[number]) => {
    const override = formattingRows.find((r) => matchesHouseKey(r, rule.key));
    const state = override ? ((override.body as 'on' | 'off') ?? rule.houseDefault) : rule.houseDefault;
    const next = state === 'on' ? 'off' : 'on';
    if (override) actions.editRow(override.id, { body: next });
    else actions.addRow({ kind: 'formatting_rule', title: rule.key, body: next });
  };

  // Removes EVERY live row for this key, not just the first — self-heals any
  // pre-existing duplicates. Each removal is id-keyed (§B invariant).
  const resetFormatting = (rule: (typeof HOUSE_FORMATTING_RULES)[number]) => {
    formattingRows.filter((r) => matchesHouseKey(r, rule.key)).forEach((r) => actions.removeRow(r.id));
  };

  return (
    <div className="flex flex-col gap-3.5">
      {/* 1 — Voice summary: always injected, never collapsed */}
      <StepCard
        title="Voice summary"
        headerRight={
          <>
            <span className="text-xs text-muted-foreground">always injected into every content task</span>
            <span
              className={cn(
                'font-mono text-[11px] tabular-nums',
                summaryWords > 150 ? 'text-destructive' : 'text-teal',
              )}
            >
              {summaryWords} / 150 words
            </span>
          </>
        }
      >
        <div className="p-4">
          {isDraftSummary && canVerify && (
            <button
              type="button"
              onClick={() => void verifyEntry('voice_profile', voice.entry!.id, undefined, bbTenant)}
              className="mb-2 inline-flex items-center gap-1 rounded-full bg-coral-light px-2.5 py-0.5 text-[10.5px] font-semibold text-coral-dark hover:bg-coral-light/70"
            >
              found on site · Verify
            </button>
          )}
          <textarea
            className={cn(inputClass, 'min-h-[88px] resize-y leading-[21px]')}
            value={summaryText}
            // Buffers only — commits to voice_profile.voice_summary on Save,
            // and never back to the legacy tone_of_writing field (see the
            // legacyToneFallback doc above).
            onChange={(e) =>
              actions.setField('voice_summary', e.target.value, storedScalar('voice_summary'))
            }
          />
          {voice.entry && !isDraftSummary && (
            <div className="mt-2">
              <VerificationRow
                verifiedBy={voice.entry.verified_by}
                verifiedAt={voice.entry.verified_at}
                canVerify={canVerify}
                onVerify={() => void verifyEntry('voice_profile', voice.entry!.id, undefined, bbTenant)}
              />
            </div>
          )}
        </div>
      </StepCard>

      {/* 2 — Attributes: 5-7 adjective chips, add/remove */}
      <StepCard title="Attributes" headerRight={<span className="text-xs text-muted-foreground">5–7 adjectives · scores at 5</span>}>
        <div className="flex flex-wrap gap-2 p-4">
          {attributes.map((a) => (
            <span
              key={a}
              className="inline-flex items-center gap-1 rounded-full border border-coral/25 bg-coral-light px-3 py-1 text-xs font-medium text-coral-dark"
            >
              {a}
              <button
                type="button"
                aria-label={`Remove ${a}`}
                onClick={() => actions.removeAttr(a)}
                className="text-coral-dark/60 hover:text-coral-dark"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          <input
            className="rounded-full border border-dashed border-input bg-warm-bg px-3 py-1 text-xs text-teal outline-none focus:border-coral"
            placeholder="+ add attribute"
            value={newAttr}
            onChange={(e) => setNewAttr(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newAttr.trim()) {
                actions.addAttr(newAttr.trim());
                setNewAttr('');
              }
            }}
          />
        </div>
      </StepCard>

      {/* 3 — Mechanics: person (segmented), customer term, reading level, humor */}
      <StepCard title="Mechanics">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 p-4">
          <Field label="Person">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-input bg-warm-bg p-0.5">
                {(['We', 'I'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => actions.setField('person', p, storedScalar('person'))}
                    className={cn(
                      'rounded-[6px] px-3.5 py-1.5 text-xs font-semibold transition-colors',
                      isEnumPerson(personView) && personView.trim().toLowerCase() === p.toLowerCase()
                        ? 'bg-coral text-white'
                        : 'text-teal-dark hover:bg-teal-light',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
              {/* Stored value this control cannot represent — show it verbatim
                  rather than leaving the field looking unset (which invites an
                  operator to overwrite real data). Read-only: no coercion, no
                  clearing, no write. Enum normalization is reconcile-era work. */}
              {storedForeignPerson && (
                <span
                  data-testid="person-foreign-value"
                  title={storedForeignPerson}
                  className="inline-flex max-w-[22rem] items-center gap-1.5 truncate rounded-full border border-teal/30 bg-teal-light px-2.5 py-1 text-[11px] text-teal-dark"
                >
                  <span className="flex-none font-semibold">custom (from enrichment)</span>
                  <span className="truncate">{storedForeignPerson}</span>
                </span>
              )}
              {/* Buffering is what makes this warnable at all: under the old
                  blur-saves the overwrite had already happened by the time
                  anything could say so. */}
              {storedForeignPerson && buffer.fields.person !== undefined && (
                <span
                  data-testid="person-overwrite-warning"
                  className="text-[11px] font-semibold text-destructive"
                >
                  Save replaces the custom value with “{buffer.fields.person}”
                </span>
              )}
            </div>
          </Field>
          <Field label="Customer term">
            <input
              className={inputClass}
              value={scalar('customer_term')}
              placeholder="e.g. homeowners"
              onChange={(e) =>
                actions.setField('customer_term', e.target.value, storedScalar('customer_term'))
              }
            />
          </Field>
          <Field label="Reading level">
            <input
              className={inputClass}
              value={scalar('reading_level')}
              placeholder="e.g. Grade 6–8"
              onChange={(e) =>
                actions.setField('reading_level', e.target.value, storedScalar('reading_level'))
              }
            />
          </Field>
          <Field label="Humor level">
            <input
              className={inputClass}
              value={scalar('humor_level')}
              placeholder="e.g. Dry · social only"
              onChange={(e) =>
                actions.setField('humor_level', e.target.value, storedScalar('humor_level'))
              }
            />
          </Field>
        </div>
      </StepCard>

      {/* 4 — Do say / never say chip groups */}
      <div className="grid grid-cols-2 gap-3.5">
        <StepCard title="Do say" headerRight={<span className="text-xs text-muted-foreground">each phrase its own entry</span>}>
          <div className="flex flex-wrap gap-2 p-4">
            {doSay.map((c) => (
              <span
                key={c.id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium',
                  c.isNew
                    ? 'border-dashed border-[hsl(var(--status-warning)/0.5)] bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]'
                    : 'border-[hsl(var(--status-success)/0.3)] bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]',
                )}
              >
                {c.body}
                {c.isNew && <UnsavedTag />}
                <button
                  type="button"
                  aria-label={`Remove ${c.body}`}
                  onClick={() => actions.removeRow(c.id)}
                  className="opacity-60 hover:opacity-100"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <input
              className="rounded-full border border-dashed border-input bg-warm-bg px-3 py-1 text-xs text-teal outline-none focus:border-coral"
              placeholder="+ add phrase"
              value={newDo}
              onChange={(e) => setNewDo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDo.trim()) {
                  actions.addRow({ kind: 'do_say', body: newDo.trim() });
                  setNewDo('');
                }
              }}
            />
          </div>
        </StepCard>

        <StepCard title="Never say" headerRight={<span className="text-xs text-muted-foreground">scores at 3+</span>}>
          <div className="flex flex-wrap gap-2 p-4">
            {neverSay.map((c) => (
              <button
                key={c.id}
                type="button"
                title="Remove"
                aria-label={`Remove ${c.body}`}
                onClick={() => actions.removeRow(c.id)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium line-through',
                  c.isNew
                    ? 'border-dashed border-[hsl(var(--status-warning)/0.5)] bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]'
                    : 'border-destructive/25 bg-destructive/10 text-destructive',
                )}
              >
                {c.body}
                {c.isNew && <UnsavedTag />}
              </button>
            ))}
            <input
              className="rounded-full border border-dashed border-input bg-warm-bg px-3 py-1 text-xs text-teal outline-none focus:border-coral"
              placeholder="+ add phrase"
              value={newNever}
              onChange={(e) => setNewNever(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newNever.trim()) {
                  actions.addRow({ kind: 'never_say', body: newNever.trim() });
                  setNewNever('');
                }
              }}
            />
          </div>
        </StepCard>
      </div>

      {/* 5 — Formatting rules: inherit Agile house rules, client-overridable */}
      <StepCard title="Formatting rules" headerRight={<span className="text-xs text-muted-foreground">Agile house rules · override per client</span>}>
        <ul className="divide-y divide-border/60">
          {HOUSE_FORMATTING_RULES.map((rule) => {
            const override = formattingRows.find((r) => matchesHouseKey(r, rule.key));
            const state = override ? ((override.body as 'on' | 'off') ?? rule.houseDefault) : rule.houseDefault;
            return (
              <li key={rule.key} className="flex items-center gap-3 px-4 py-2.5">
                <span className="flex-1 text-[13px]">{rule.label}</span>
                {override ? (
                  <>
                    <EnvelopeBadge kind="auto">overridden</EnvelopeBadge>
                    {(override.isNew || override.isEdited) && <UnsavedBadge />}
                    <button
                      type="button"
                      onClick={() => resetFormatting(rule)}
                      className="text-[11px] font-semibold text-teal hover:text-coral"
                    >
                      Reset to house default
                    </button>
                  </>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    inherited · house default {rule.houseDefault.toUpperCase()}
                  </span>
                )}
                <button
                  type="button"
                  role="switch"
                  aria-checked={state === 'on'}
                  aria-label={rule.label}
                  onClick={() => toggleFormatting(rule)}
                  className={cn(
                    'relative h-5 w-9 flex-shrink-0 rounded-full transition-colors',
                    state === 'on' ? 'bg-coral' : 'bg-teal-light',
                  )}
                >
                  <span
                    className={cn(
                      // left-0 pins the thumb's origin: without it the absolute
                      // span's `left:auto` resolves to its ~18px static position
                      // and stacks with the translate below (18+18=36px), so the
                      // thumb started at the track's right edge and overflowed it.
                      'absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                      state === 'on' ? 'translate-x-[18px]' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </li>
            );
          })}
          {/* Live rows the title===key lookup above cannot place. They are real
              client data; hiding them made §B score 22/22 over content the
              operator could never see. Read-only: displayed, not editable —
              this control set only understands the four house keys. */}
          {formattingRows
            .filter((r) => !HOUSE_FORMATTING_RULES.some((h) => matchesHouseKey(r, h.key)))
            .map((row) => (
              <li
                key={row.id}
                data-testid="formatting-unmapped-row"
                className="flex items-start justify-between gap-3 border-t border-border px-4 py-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-medium">
                    {formattingFallbackTitle(row.title, row.body)}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    custom rule from enrichment · not one of the four house rules
                  </div>
                </div>
                <EnvelopeBadge kind="source">read-only</EnvelopeBadge>
              </li>
            ))}
        </ul>
      </StepCard>

      {/* 6 — Golden examples: elevated cards, below always-injected config */}
      <StepCard
        className="border-coral/30 bg-coral-light/30"
        title="Golden examples"
        headerRight={
          <>
            <EnvelopeBadge kind="source">{golden.length} of 10</EnvelopeBadge>
            <span className="text-xs text-muted-foreground">approved copy + why it&rsquo;s on-voice · 1 pt each</span>
          </>
        }
      >
        <ul>
          {golden.map((g) => (
            <li key={g.id} className="border-b border-coral/15 px-4 py-3 last:border-b-0">
              <div className="text-[15px] font-medium leading-[21px]">&ldquo;{g.body}&rdquo;</div>
              {g.why && (
                <div className="mt-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-teal-dark">Why it&rsquo;s on-voice:</span> {g.why}
                </div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {g.channel && <EnvelopeBadge kind="source">{g.channel}</EnvelopeBadge>}
                {g.isNew ? (
                  <>
                    <UnsavedBadge />
                    <button
                      type="button"
                      aria-label={`Remove ${g.body}`}
                      onClick={() => actions.removeRow(g.id)}
                      className="text-[11px] font-semibold text-teal hover:text-coral"
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    {!g.verified_by && (
                      <span className="text-[11px] font-medium text-coral-dark">Does this sound like you?</span>
                    )}
                    <VerificationRow
                      verifiedBy={g.verified_by}
                      canVerify={canVerify}
                      onVerify={() => void verifyEntry('chunks', g.id, undefined, bbTenant)}
                    />
                    {!g.verified_by && canVerify && (
                      <button
                        type="button"
                        onClick={() => actions.reclassifyToRejected(g.id)}
                        className="text-[11px] font-semibold text-teal hover:text-teal-dark"
                      >
                        Reject
                      </button>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2 p-4">
          <textarea
            className={cn(inputClass, 'min-h-[56px] resize-y')}
            placeholder="Paste approved copy…"
            value={gx.body}
            onChange={(e) => setGx({ ...gx, body: e.target.value })}
          />
          <div className="flex gap-2">
            <input
              className={cn(inputClass, 'max-w-[180px]')}
              placeholder="channel (google ads…)"
              value={gx.channel}
              onChange={(e) => setGx({ ...gx, channel: e.target.value })}
            />
            <input
              className={inputClass}
              placeholder="why it's on-voice"
              value={gx.why}
              onChange={(e) => setGx({ ...gx, why: e.target.value })}
            />
            <button
              type="button"
              onClick={addGolden}
              className="flex-none rounded-lg bg-coral px-3.5 py-2 text-xs font-semibold text-white hover:bg-coral-hover"
            >
              Add example
            </button>
          </div>
        </div>
      </StepCard>

      {/* 7 — Rejected examples: last, quiet */}
      <StepCard
        className="opacity-75"
        title="Rejected examples"
        headerRight={<span className="text-xs text-muted-foreground">negative examples teach faster than positive ones</span>}
      >
        <ul>
          {rejected.map((r) => (
            <li
              key={r.id}
              data-testid={isInternalRejectedNote(r) ? 'rejected-internal-note' : 'rejected-client-example'}
              className="border-b border-border/40 px-4 py-3 last:border-b-0"
            >
              <div className="flex items-start gap-2">
                <div
                  className={cn(
                    'flex-1 text-[14px] leading-[21px] text-muted-foreground',
                    !isInternalRejectedNote(r) && 'line-through',
                  )}
                >
                  {isInternalRejectedNote(r) ? r.body : <>&ldquo;{r.body}&rdquo;</>}
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${r.body}`}
                  onClick={() => actions.removeRow(r.id)}
                  className="mt-0.5 opacity-60 hover:opacity-100"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              {r.why && !isInternalRejectedNote(r) && (
                <div className="mt-1 text-xs text-muted-foreground/80">
                  <span className="font-semibold">Client&rsquo;s reason:</span> {r.why}
                </div>
              )}
              {r.why && isInternalRejectedNote(r) && (
                <div className="mt-1 text-xs text-muted-foreground/80">{r.why}</div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {isInternalRejectedNote(r) ? (
                  <EnvelopeBadge kind="source">internal · not client copy</EnvelopeBadge>
                ) : (
                  <EnvelopeBadge kind="rejected">client rejected</EnvelopeBadge>
                )}
                {(r.isNew || r.isEdited) && <UnsavedBadge />}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2 p-4">
          <textarea
            className={cn(inputClass, 'min-h-[56px] resize-y')}
            placeholder="Paste rejected copy…"
            value={newRejected.body}
            onChange={(e) => setNewRejected({ ...newRejected, body: e.target.value })}
          />
          <div className="flex gap-2">
            <input
              className={inputClass}
              placeholder="why it was rejected"
              value={newRejected.why}
              onChange={(e) => setNewRejected({ ...newRejected, why: e.target.value })}
            />
            <button
              type="button"
              onClick={addRejected}
              className="flex-none rounded-lg bg-teal px-3.5 py-2 text-xs font-semibold text-white hover:bg-teal-hover"
            >
              Add example
            </button>
          </div>
        </div>
      </StepCard>
    </div>
  );
}
